import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { SunPosition } from 'lavva.exalushome/build/js/Services/Scenes/LeftArgumentTypes';
import { ConditionsTypes } from 'lavva.exalushome/build/js/Services/Scenes/Scenes';
import { SupportedArgumentTypes } from 'lavva.exalushome/build/js/Services/Scenes/ScenesBuilder';
import { IconSunDown, IconSunUp } from '../../../../../../../../assets/icons';
import { IconActions } from '../../../../../../../../assets/navigation-icons/icon-actions';
import { AdvanceDelayDialog, InputMui, InputSelect, SubmitButton } from '../../../../../../../../components';
import { TriggerMethodType } from '../../../../../../../../types/actions';
import { useExalusSunTimes } from '../../../../../../hooks/scenarios';
import { ConditionTypeSelect } from '../../../../components/condition-type-select';
import { TriggerMethodSelect } from '../../../../components/trigger-method-select';
import { useExalusCreateActionContext } from '../../../../context';
import { useConditionEdit } from '../../../../hooks/use-condition-edit';
import { useActionParameterOptions } from '../../../../utils/use-action-parameter-options';
import SunChart from './chart';

export const AstronomicalSource: React.FC = () => {
  const { t } = useTranslation('action');
  const [sunPosition, setSunPosition] = useState<SunPosition>(SunPosition.SunRise);
  const [showDelayDialog, setShowDelayDialog] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const { sunPositions } = useActionParameterOptions();
  const { builder } = useExalusCreateActionContext();
  const { data } = useExalusSunTimes();
  const {
    seq,
    triggerMethod,
    conditionType,
    changeableAtMeetCondition,
    setConditionType,
    updateTriggerMethod,
    finalizeSequenceSubmit,
  } = useConditionEdit({ supportedParam: SupportedArgumentTypes.ArgumentAsAstronomicalClockWithOffset });

  useEffect(() => {
    const argument = seq?.LeftArgument.Argument?.ArgumentAsAstronomicalClockWithOffset;

    if (argument) {
      setSunPosition(argument.Position);
      setOffset(argument.Offset / 60);
    }
  }, [seq]);

  const onChangeSunPosition = (sunPosition: SunPosition) => setSunPosition(sunPosition);

  const handleSubmit = () => {
    const argument = builder?.ArgumentAsAstronomicalClockWithOffset(
      { Position: sunPosition, Offset: offset * 60 },
      triggerMethod === TriggerMethodType.WhenChangeOn,
      conditionType,
    );

    finalizeSequenceSubmit(argument, -3);
  };

  const nextFireTime = useMemo(() => {
    if (data) {
      return sunPosition === SunPosition.SunRise
        ? data.sunRiseTime.getTime() + offset * 60000
        : data.sunSetTime.getTime() + offset * 60000;
    }
  }, [data, offset, sunPosition]);

  return (
    <>
      <div className="grid-list-24">
        {changeableAtMeetCondition ? (
          <TriggerMethodSelect triggerMethod={triggerMethod} updateTriggerMethod={updateTriggerMethod} />
        ) : null}
        {triggerMethod === TriggerMethodType.IfIsIt && (
          <ConditionTypeSelect
            conditionType={conditionType}
            setConditionType={setConditionType}
            astronomical
            allow={[
              ConditionsTypes.Equal,
              ConditionsTypes.NotEqueal,
              ConditionsTypes.BiggerThan,
              ConditionsTypes.BiggerThanOrEqual,
              ConditionsTypes.SmallerThan,
              ConditionsTypes.SmallerThanOrEqual,
            ]}
          />
        )}
        <InputSelect
          options={sunPositions}
          value={sunPosition}
          onChange={onChangeSunPosition}
          label={t('action.create.conditions.sources.schedule.sunPosition')}
        />
        <InputMui
          value={offset.toString()}
          readOnly
          label={t('action.create.conditions.sources.schedule.minOffset')}
          onClickInput={() => setShowDelayDialog(true)}
        />
      </div>
      {data && (
        <div className="row-container">
          <div className="row-container--icon">
            <IconActions />
          </div>
          <p className="secondary-text">
            {t('action.create.conditions.sources.schedule.fireAt', {
              time: nextFireTime ? format(nextFireTime, 'dd.MM, HH:mm') : '-',
            })}
          </p>
        </div>
      )}
      {data && (
        <div className="row-container flex-end">
          <div className="row-container  margin-right">
            <div className="row-container--icon">
              <IconSunUp />
            </div>
            <p className="secondary-text small">{format(data?.sunRiseTime, 'dd.MM, HH:mm')}</p>
          </div>
          <div className="row-container">
            <div className="row-container--icon">
              <IconSunDown />
            </div>
            <p className="secondary-text small">{format(data?.sunSetTime, 'dd.MM, HH:mm')}</p>
          </div>
        </div>
      )}
      <SunChart time={nextFireTime} />
      <SubmitButton onClick={handleSubmit} />
      <AdvanceDelayDialog
        open={showDelayDialog}
        setOpen={setShowDelayDialog}
        title={t('action.create.conditions.sources.schedule.minOffset')}
        onSave={(value) => setOffset(value)}
        time={offset}
        min={-60}
        max={60}
        loop={false}
      />
    </>
  );
};
